/**
 * buildClientOfferReviewLabels
 *
 * Resolves every `ClientOfferReview*Labels` bag the redesigned v2 shell
 * needs, through i18next — the client-offer-review-* components stay
 * translation-agnostic (props-only), matching the convention already used
 * by `client-offer-view-labels.ts` for the v1 flow.
 */
import type { OffersT } from '../client-offer-view-labels';
import {
  type ClientOfferReviewBannerModelLabels,
  type ClientOfferReviewConfirmationModalsLabels,
  type ClientOfferReviewFilterPanelLabels,
  type ClientOfferReviewFooterBarLabels,
  type ClientOfferReviewHeaderLabels,
  type ClientOfferReviewLightboxLabels,
  type ClientOfferReviewMapPanelLabels,
  type ClientOfferReviewStatusBannerLabels,
  type ClientOfferReviewSummaryStatsLabels,
  type ClientOfferReviewToolbarLabels,
  type ClientOfferReviewUnitCardLabels,
  type ClientOfferReviewUnitListLabels,
} from '../../client-offer-review';

const P = 'clientOfferReviewV2' as const;

function buildHeaderLabels(t: OffersT): ClientOfferReviewHeaderLabels {
  return {
    brandName: t(`${P}.header.brandName`),
    loggedInAs: t(`${P}.header.loggedInAs`),
    versionPrefix: t(`${P}.header.versionPrefix`),
  };
}

function buildBannerModelLabels(t: OffersT): ClientOfferReviewBannerModelLabels {
  return {
    acceptedTitle: t(`${P}.banner.acceptedTitle`),
    acceptedDescription: (accCount, accValueLabel) =>
      t(`${P}.banner.acceptedDescription`, { count: accCount, value: accValueLabel }),
    rejectedTitle: t(`${P}.banner.rejectedTitle`),
    rejectedDescription: t(`${P}.banner.rejectedDescription`),
    expiredTitle: t(`${P}.banner.expiredTitle`),
    expiredDescription: t(`${P}.banner.expiredDescription`),
    previewTitle: t(`${P}.banner.previewTitle`),
    previewDescription: t(`${P}.banner.previewDescription`),
    pendingTitle: t(`${P}.banner.pendingTitle`),
    statusAccepted: t(`${P}.banner.statusAccepted`),
    statusRejected: t(`${P}.banner.statusRejected`),
    statusExpired: t(`${P}.banner.statusExpired`),
    statusSent: t(`${P}.banner.statusSent`),
  };
}

function buildStatusBannerLabels(t: OffersT): ClientOfferReviewStatusBannerLabels {
  return {
    diffTitle: (prevVersion) => t(`${P}.statusBanner.diffTitle`, { prevVersion }),
    diffCollapse: t(`${P}.statusBanner.diffCollapse`),
    diffShow: (count) => t(`${P}.statusBanner.diffShow`, { count }),
    diffTagPrice: t(`${P}.statusBanner.diffTagPrice`),
    diffTagRemoved: t(`${P}.statusBanner.diffTagRemoved`),
    diffTagAdded: t(`${P}.statusBanner.diffTagAdded`),
  };
}

function buildSummaryStatsLabels(t: OffersT): ClientOfferReviewSummaryStatsLabels {
  return {
    unitsLabel: t(`${P}.summaryStats.unitsLabel`),
    citiesUnit: t(`${P}.summaryStats.citiesUnit`),
    campaignLabel: t(`${P}.summaryStats.campaignLabel`),
    totalNetLabel: t(`${P}.summaryStats.totalNetLabel`),
    accepted: t(`${P}.summaryStats.accepted`),
    rejected: t(`${P}.summaryStats.rejected`),
    pending: t(`${P}.summaryStats.pending`),
    pendingHint: t(`${P}.summaryStats.pendingHint`),
    acceptedValueLabel: t(`${P}.summaryStats.acceptedValueLabel`),
    breakdownSummary: (accCount, pctAcc, rejCount, pctRej, pendCount, pctPend) =>
      t(`${P}.summaryStats.breakdownSummary`, {
        accCount,
        pctAcc,
        rejCount,
        pctRej,
        pendCount,
        pctPend,
      }),
    availableUnitsSuffix: (availCount) =>
      t(`${P}.summaryStats.availableUnitsSuffix`, { availCount }),
  };
}

function buildFilterPanelLabels(t: OffersT): ClientOfferReviewFilterPanelLabels {
  return {
    city: t(`${P}.filterPanel.city`),
    format: t(`${P}.filterPanel.format`),
    price: t(`${P}.filterPanel.price`),
    priceUnit: t(`${P}.filterPanel.priceUnit`),
    voivodeship: t(`${P}.filterPanel.voivodeship`),
    supplier: t(`${P}.filterPanel.supplier`),
    width: t(`${P}.filterPanel.width`),
    height: t(`${P}.filterPanel.height`),
    dimensionUnit: t(`${P}.filterPanel.dimensionUnit`),
    panel: {
      allLabel: t(`${P}.filterPanel.panel.allLabel`),
      searchPlaceholder: t(`${P}.filterPanel.panel.searchPlaceholder`),
      getSelectedCountLabel: (count) => t(`${P}.filterPanel.panel.selectedCount`, { count }),
      getClearAriaLabel: (fieldId) => t(`${P}.filterPanel.panel.clearAriaLabel`, { fieldId }),
      clearAllLabel: t(`${P}.filterPanel.panel.clearAllLabel`),
      addFilterLabel: t(`${P}.filterPanel.panel.addFilterLabel`),
    },
    range: {
      minLabel: t(`${P}.filterPanel.range.minLabel`),
      maxLabel: t(`${P}.filterPanel.range.maxLabel`),
      applyLabel: t(`${P}.filterPanel.range.applyLabel`),
      clearLabel: t(`${P}.filterPanel.range.clearLabel`),
    },
  };
}

function buildToolbarLabels(t: OffersT): ClientOfferReviewToolbarLabels {
  return {
    searchPlaceholder: t(`${P}.toolbar.searchPlaceholder`),
    filterAll: t(`${P}.toolbar.filterAll`),
    filterAccepted: t(`${P}.toolbar.filterAccepted`),
    filterRejected: t(`${P}.toolbar.filterRejected`),
    filterPending: t(`${P}.toolbar.filterPending`),
    photoModeAriaLabel: t(`${P}.toolbar.photoModeAriaLabel`),
    photoModeLabel: t(`${P}.toolbar.photoModeLabel`),
    photoModeLarge: t(`${P}.toolbar.photoModeLarge`),
    photoModeSmall: t(`${P}.toolbar.photoModeSmall`),
    photoModeNone: t(`${P}.toolbar.photoModeNone`),
    sortAriaLabel: t(`${P}.toolbar.sortAriaLabel`),
    sortLabel: t(`${P}.toolbar.sortLabel`),
    sortByCity: t(`${P}.toolbar.sortByCity`),
    sortByPrice: t(`${P}.toolbar.sortByPrice`),
    sortByDecision: t(`${P}.toolbar.sortByDecision`),
    granularityAriaLabel: t(`${P}.toolbar.granularityAriaLabel`),
    granularityLabel: t(`${P}.toolbar.granularityLabel`),
    granularityHalfMonth: t(`${P}.toolbar.granularityHalfMonth`),
    granularityMonth: t(`${P}.toolbar.granularityMonth`),
    granularityWeek: t(`${P}.toolbar.granularityWeek`),
    granularityDay: t(`${P}.toolbar.granularityDay`),
    selectAllAriaLabel: t(`${P}.toolbar.selectAllAriaLabel`),
    selectAllHint: t(`${P}.toolbar.selectAllHint`),
    clearAllAriaLabel: t(`${P}.toolbar.clearAllAriaLabel`),
    clearAllHint: t(`${P}.toolbar.clearAllHint`),

    pageSizeAriaLabel: t(`${P}.toolbar.pageSizeAriaLabel`),
    pageSizeLabel: t(`${P}.toolbar.pageSizeLabel`),
    pageSizeAllOption: t(`${P}.toolbar.pageSizeAllOption`),
    pageSizeCustomAriaLabel: t(`${P}.toolbar.pageSizeCustomAriaLabel`),
    pageSizeCustomPlaceholder: t(`${P}.toolbar.pageSizeCustomPlaceholder`),
  };
}

function buildUnitCardLabels(t: OffersT): ClientOfferReviewUnitCardLabels {
  return {
    soldLabel: t(`${P}.unitCard.soldLabel`),
    acceptedBadge: t(`${P}.unitCard.acceptedBadge`),
    rejectedBadge: t(`${P}.unitCard.rejectedBadge`),
    priceConfirmationPending: t(`${P}.unitCard.priceConfirmationPending`),
    netSuffix: t(`${P}.unitCard.netSuffix`),
    locateAction: t(`${P}.unitCard.locateAction`),
    acceptAction: t(`${P}.unitCard.acceptAction`),
    undoAcceptAction: t(`${P}.unitCard.undoAcceptAction`),
    acceptDisabledHint: t(`${P}.unitCard.acceptDisabledHint`),
    rejectAction: t(`${P}.unitCard.rejectAction`),
    undoRejectAction: t(`${P}.unitCard.undoRejectAction`),
    lockedHint: t(`${P}.unitCard.lockedHint`),
    seeDetails: t(`${P}.unitCard.seeDetails`),
    grwNumberLabel: t(`${P}.unitCard.grwNumberLabel`),
    discountedPriceLabel: t(`${P}.unitCard.discountedPriceLabel`),
    periodChips: {
      free: t(`${P}.periodChips.free`),
      reserved: t(`${P}.periodChips.reserved`),
      sold: t(`${P}.periodChips.sold`),
      selected: t(`${P}.periodChips.selected`),
      selectedRangeSummary: (selectedCount, totalCount) =>
        t(`${P}.periodChips.selectedRangeSummary`, { selectedCount, totalCount }),
      toggleAriaLabel: (periodLabel) => t(`${P}.periodChips.toggleAriaLabel`, { periodLabel }),
      willAcceptHint: t(`${P}.periodChips.willAcceptHint`),
    },
    photoBlock: {
      zoomHint: t(`${P}.photoBlock.zoomHint`),
      soldLabel: t(`${P}.photoBlock.soldLabel`),
      placeholderCaptionLarge: t(`${P}.photoBlock.placeholderCaptionLarge`),
      placeholderCaptionSmall: t(`${P}.photoBlock.placeholderCaptionSmall`),
    },
  };
}

function buildUnitListLabels(t: OffersT): ClientOfferReviewUnitListLabels {
  return {
    noResultsTitle: t(`${P}.unitList.noResultsTitle`),
    clearFilters: t(`${P}.unitList.clearFilters`),
    unitCard: buildUnitCardLabels(t),
    pagination: {
      previous: t(`${P}.pagination.previous`),
      next: t(`${P}.pagination.next`),
      pageAriaLabel: (page) => t(`${P}.pagination.pageAriaLabel`, { page }),
    },
  };
}

function buildConfirmationModalsLabels(t: OffersT): ClientOfferReviewConfirmationModalsLabels {
  return {
    selectAllTitle: t(`${P}.confirmationModals.selectAllTitle`),
    selectAllBody: (availCount) => t(`${P}.confirmationModals.selectAllBody`, { availCount }),
    selectAllCancel: t(`${P}.confirmationModals.selectAllCancel`),
    selectAllConfirm: t(`${P}.confirmationModals.selectAllConfirm`),
    clearAllTitle: t(`${P}.confirmationModals.clearAllTitle`),
    clearAllBody: (accCount, rejCount) =>
      t(`${P}.confirmationModals.clearAllBody`, { accCount, rejCount }),
    clearAllCancel: t(`${P}.confirmationModals.clearAllCancel`),
    clearAllConfirm: t(`${P}.confirmationModals.clearAllConfirm`),
    acceptOfferTitle: (versionNumber) =>
      t(`${P}.confirmationModals.acceptOfferTitle`, { versionNumber }),
    acceptOfferBody: (accCount, rejCount, pendCount, accValueLabel) =>
      t(`${P}.confirmationModals.acceptOfferBody`, {
        accCount,
        rejCount,
        pendCount,
        value: accValueLabel,
      }),
    acceptOfferCancel: t(`${P}.confirmationModals.acceptOfferCancel`),
    acceptOfferConfirm: t(`${P}.confirmationModals.acceptOfferConfirm`),
  };
}

function buildLightboxLabels(t: OffersT): ClientOfferReviewLightboxLabels {
  return {
    close: t(`${P}.lightbox.close`),
    previous: t(`${P}.lightbox.previous`),
    next: t(`${P}.lightbox.next`),
    frames: [t(`${P}.lightbox.frame1`), t(`${P}.lightbox.frame2`), t(`${P}.lightbox.frame3`)],
  };
}

function buildMapPanelLabels(t: OffersT): ClientOfferReviewMapPanelLabels {
  return {
    fullscreenToggle: t(`${P}.mapPanel.fullscreenToggle`),
    growListWidth: t(`${P}.mapPanel.growListWidth`),
    growMapWidth: t(`${P}.mapPanel.growMapWidth`),
    legendAccepted: t(`${P}.mapPanel.legendAccepted`),
    legendRejected: t(`${P}.mapPanel.legendRejected`),
    legendDigital: t(`${P}.mapPanel.legendDigital`),
    legendOther: t(`${P}.mapPanel.legendOther`),
    legendSold: t(`${P}.mapPanel.legendSold`),
  };
}

function buildFooterBarLabels(t: OffersT): ClientOfferReviewFooterBarLabels {
  return {
    disclaimer: t(`${P}.footerBar.disclaimer`),
    summary: (accCount, rejCount, pendCount, accValueLabel) =>
      t(`${P}.footerBar.summary`, { accCount, rejCount, pendCount, value: accValueLabel }),
    accept: (versionNumber) => t(`${P}.footerBar.accept`, { versionNumber }),
    customPeriodsChip: t(`${P}.footerBar.customPeriodsChip`),
    customPeriodsCount: (count) => t(`${P}.footerBar.customPeriodsCount`, { count }),
    customPeriodsTitle: t(`${P}.footerBar.customPeriodsTitle`),
    customPeriodsRestoreHint: t(`${P}.footerBar.customPeriodsRestoreHint`),
    restoreCustomPeriods: t(`${P}.footerBar.restoreCustomPeriods`),
    acceptDisabledNoAcceptedHint: t(`${P}.footerBar.acceptDisabledNoAcceptedHint`),
  };
}

export interface ClientOfferReviewLabelBundle {
  header: ClientOfferReviewHeaderLabels;
  bannerModel: ClientOfferReviewBannerModelLabels;
  statusBanner: ClientOfferReviewStatusBannerLabels;
  summaryStats: ClientOfferReviewSummaryStatsLabels;
  filterPanel: ClientOfferReviewFilterPanelLabels;
  toolbar: ClientOfferReviewToolbarLabels;
  unitList: ClientOfferReviewUnitListLabels;
  confirmationModals: ClientOfferReviewConfirmationModalsLabels;
  lightbox: ClientOfferReviewLightboxLabels;
  mapPanel: ClientOfferReviewMapPanelLabels;
  footerBar: ClientOfferReviewFooterBarLabels;
  inquirySubmit: string;
  inquirySuccess: string;
}

/** Resolves every ClientOfferReview*Labels bag the v2 shell needs, through i18next. */
export function buildClientOfferReviewLabels(t: OffersT): ClientOfferReviewLabelBundle {
  return {
    header: buildHeaderLabels(t),
    bannerModel: buildBannerModelLabels(t),
    statusBanner: buildStatusBannerLabels(t),
    summaryStats: buildSummaryStatsLabels(t),
    filterPanel: buildFilterPanelLabels(t),
    toolbar: buildToolbarLabels(t),
    unitList: buildUnitListLabels(t),
    confirmationModals: buildConfirmationModalsLabels(t),
    lightbox: buildLightboxLabels(t),
    mapPanel: buildMapPanelLabels(t),
    footerBar: buildFooterBarLabels(t),
    inquirySubmit: t(`${P}.inquiry.submit`),
    inquirySuccess: t(`${P}.inquiry.success`),
  };
}
